import React from "react"
import { Link } from "react-router-dom"
import { ArrowRight, Search, MessageSquare, RefreshCw } from "lucide-react"
import SectionHeading from "./SectionHeading.jsx"
import GoogleProfileReviewCTA from "../GoogleProfileReviewCTA.jsx"
import { CHATBOT_PATH, SEO_GEO_LABEL } from "../../data/services.js"

const PANELS = [
  {
    id: "search-visibility",
    icon: Search,
    title: SEO_GEO_LABEL,
    lead: "Strengthen your presence across Google search and AI-powered discovery through technical optimisation, authoritative content and a clear digital footprint.",
    points: [
      "Technical and on-page SEO",
      "Local SEO for Adelaide and service areas",
      "Google Business Profile optimisation",
      "Clear, consistent business information for AI-powered search",
      "Google Ads management and conversion tracking",
    ],
    links: [
      { to: "/seo-adelaide", label: "SEO & GEO" },
      { to: "/google-ads-adelaide", label: "Google Ads in Adelaide" },
    ],
  },
  {
    id: "integrations",
    icon: MessageSquare,
    title: "AI Chatbots & Business Automation",
    lead: "Help customers find answers, navigate your services and take the next step with intelligent website assistants and connected business workflows.",
    points: [
      "AI website assistants that use your approved business content",
      "Lead capture, qualification and human handover",
      "CRM and customer enquiry workflows",
      "Booking and scheduling platforms",
      "E-commerce, administration and reporting tools",
    ],
    links: [
      { to: CHATBOT_PATH, label: "AI chatbots" },
      { to: "/business-integrations", label: "Business integrations" },
    ],
  },
  {
    id: "support",
    icon: RefreshCw,
    title: "Ongoing digital partnership",
    lead: "Your website and campaigns keep improving after launch, with one team that knows your business.",
    points: [
      "Continuous improvement and website updates",
      "SEO & GEO monitoring and optimisation",
      "Campaign management",
      "Technical support",
      "Reporting with clear recommendations",
    ],
    links: [{ to: "/digital-support", label: "Ongoing digital support" }],
  },
]

export default function CapabilitiesSection() {
  return (
    <section aria-labelledby="capabilities-heading" className="py-20 sm:py-24">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <SectionHeading
          id="capabilities-heading"
          eyebrow="Capabilities & ongoing support"
          title="Search and AI visibility, intelligent customer engagement and ongoing support"
        />

        <div className="mt-12 grid gap-6 lg:grid-cols-3">
          {PANELS.map(({ id, icon: Icon, title, lead, points, links = [] }) => (
            <article
              key={id}
              id={id}
              aria-labelledby={`${id}-heading`}
              className="scroll-mt-24 flex flex-col rounded-xl2 border border-brand-border bg-white p-7 shadow-card"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-primarySoft text-brand-primary">
                <Icon aria-hidden="true" className="h-6 w-6" />
              </span>
              <h3 id={`${id}-heading`} className="mt-5 text-xl font-semibold text-brand-heading">
                {title}
              </h3>
              <p className="mt-3 leading-relaxed text-brand-textSoft">{lead}</p>
              <ul className="mt-5 flex-1 space-y-2.5 text-brand-text">
                {points.map((point) => (
                  <li key={point} className="flex gap-3">
                    <span
                      aria-hidden="true"
                      className="mt-2.5 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-primary"
                    />
                    {point}
                  </li>
                ))}
              </ul>
              {links.length > 0 && (
                <div className="mt-6 flex flex-col gap-2">
                  {links.map((link) => (
                    <Link
                      key={link.to}
                      to={link.to}
                      className="inline-flex items-center gap-2 font-semibold text-brand-primary hover:underline"
                    >
                      {link.label}
                      <ArrowRight aria-hidden="true" className="h-4 w-4" />
                    </Link>
                  ))}
                </div>
              )}
            </article>
          ))}
        </div>

        <div className="mt-10 flex flex-col items-start gap-4 rounded-xl2 border border-orange-200 bg-orange-50 p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-semibold text-brand-heading">
              Start with your Google Business Profile
            </h3>
            <p className="mt-1 text-brand-textSoft">
              A one-time review and optimisation to improve local visibility on Google Search and
              Maps.
            </p>
          </div>
          <GoogleProfileReviewCTA className="shrink-0 !bg-orange-700 hover:!bg-orange-800" />
        </div>
      </div>
    </section>
  )
}
